import api, { Schemas } from '~/clients/api-client'
import { dispatchErrorToast, dispatchSuccessToast } from '~/store/slices/toasterSlice'
import { useStore } from '~/store/store'
import {
    selectDepartmentsByPractitionerStatusDefinition,
    useDepartments,
    usePractitionerStatusDefinitions,
    useStatusDefinitionDepartmentAssignments,
} from '~/storm'
import { PractitionerAssignment, PractitionerStatusDefinition } from '~/utils/surgeryActivities'

import { useAppForm } from '../../Form/formHooks'
import {
    convertTitleToCode,
    createStatusDefinitionFormSchema,
    getDefaultValues,
    getStatusDefinitionColorsByAssignment,
    StatusDefinitionFormValues,
} from './formUtils'

type UseStatusDefinitionFormParams = {
    setOpenDialog: (open: boolean) => void
    assignmentName: PractitionerAssignment
    statusDefinition?: PractitionerStatusDefinition
}

type DepartmentAssignment = Schemas['StatusDefinitionDepartmentAssignmentOut']

const formValuesToApiPayload = (
    values: StatusDefinitionFormValues,
    assignmentName: PractitionerAssignment,
    existingDefinition?: PractitionerStatusDefinition
): Schemas['PractitionerStatusDefinitionCreate'] => ({
    title: values.title,
    label: values.label,
    color: values.color,
    code: existingDefinition?.code ?? convertTitleToCode(values.title),
    assignment: assignmentName,
    order: existingDefinition?.order ?? 1,
    time_of_day: values.timeOfDay,
    keyboard_shortcut: values.keyboardShortcut,
    keyboard_has_shift: values.keyboardHasShift,
    keyboard_has_meta_key: values.keyboardHasMeta,
    standard: existingDefinition?.standard ?? false,
})

async function saveNewStatusDefinition(values: StatusDefinitionFormValues, assignmentName: PractitionerAssignment) {
    const payload = formValuesToApiPayload(values, assignmentName)
    const deptToCreate = values.departments.map(departmentId => ({ status_code: payload.code, department_id: departmentId }))

    const response = await api.createPractitionerStatusDefinition(payload)
    if (response.error) {
        throw new Error('Failed to create status definition')
    }

    // Sequential because it requires status definition to exist in DB
    if (deptToCreate.length > 0) {
        await api.createBatchStatusDefinitionDepartmentAssignment(deptToCreate)
    }
}

async function saveEditStatusDefinition(
    values: StatusDefinitionFormValues,
    existingDefinition: PractitionerStatusDefinition,
    allDepartmentAssignments: DepartmentAssignment[],
    assignmentName: PractitionerAssignment
) {
    const payload = formValuesToApiPayload(values, assignmentName, existingDefinition)
    const existingDeptAssignments = allDepartmentAssignments.filter(da => da.status_code === existingDefinition.code)
    const existingDeptIds = new Set(existingDeptAssignments.map(da => da.department_id))
    const newDeptIds = new Set(values.departments)

    const deptToCreate = values.departments
        .filter(id => !existingDeptIds.has(id))
        .map(departmentId => ({ status_code: existingDefinition.code, department_id: departmentId }))
    const deptIdsToDelete = existingDeptAssignments.filter(da => !newDeptIds.has(da.department_id)).map(da => da.id)

    // Preventing partial edits
    const response = await api.updatePractitionerStatusDefinition(existingDefinition.id, payload)
    if (response.error) {
        throw new Error('Failed to update status definition')
    }
    await Promise.all([
        ...(deptToCreate.length > 0 ? [api.createBatchStatusDefinitionDepartmentAssignment(deptToCreate)] : []),
        ...(deptIdsToDelete.length > 0 ? [api.deleteBatchStatusDefinitionDepartmentAssignments(deptIdsToDelete)] : []),
    ])
}

export const useStatusDefinitionForm = ({ setOpenDialog, assignmentName, statusDefinition }: UseStatusDefinitionFormParams) => {
    const departments = useDepartments()
    const getDepartmentsByCode = useStore(selectDepartmentsByPractitionerStatusDefinition)
    const allStatusDefinitions = usePractitionerStatusDefinitions()
    const allDepartmentAssignments = useStatusDefinitionDepartmentAssignments()

    const resolvedDepartmentIds = statusDefinition ? getDepartmentsByCode(statusDefinition.code).map(d => d.id) : []
    const defaultValues = getDefaultValues(assignmentName, resolvedDepartmentIds, statusDefinition)
    const colorPalette = getStatusDefinitionColorsByAssignment(assignmentName)

    // Build existing shortcuts list, excluding the current status being edited
    const existingShortcuts = allStatusDefinitions
        .filter(s => s.code !== statusDefinition?.code && s.keyboard_shortcut)
        .map(s => ({
            keyboard_shortcut: s.keyboard_shortcut ?? '',
            keyboard_has_shift: s.keyboard_has_shift ?? false,
            keyboard_has_meta_key: s.keyboard_has_meta_key ?? false,
        }))

    const departmentOptions = departments.map(department => ({
        label: department.name,
        value: department.id,
    }))

    const form = useAppForm({
        defaultValues,
        validators: { onChange: createStatusDefinitionFormSchema(assignmentName, existingShortcuts) },
        onSubmit: async ({ value, formApi }) => {
            try {
                if (statusDefinition) {
                    await saveEditStatusDefinition(value, statusDefinition, allDepartmentAssignments, assignmentName)
                } else {
                    await saveNewStatusDefinition(value, assignmentName)
                }

                dispatchSuccessToast(statusDefinition ? 'Aktiviteten ble oppdatert' : 'Aktiviteten ble opprettet')
                formApi.reset(value)
                setOpenDialog(false)
            } catch {
                dispatchErrorToast({ message: 'Noe gikk galt ved lagring av aktiviteten. Prøv igjen senere.' })
            }
        },
    })

    const applyShortcutToForm = (shortcut: { keyboardKey: string; hasShift?: boolean; hasMeta?: boolean }) => {
        form.setFieldValue('keyboardShortcut', shortcut.keyboardKey)
        form.setFieldValue('keyboardHasShift', shortcut.hasShift ?? false)
        form.setFieldValue('keyboardHasMeta', shortcut.hasMeta ?? false)
    }

    return {
        form,
        colorPalette,
        existingShortcuts,
        departmentOptions,
        applyShortcutToForm,
    }
}
